export const ALL_ORGANIZATIONS = {
  id: 'All Organizations',
  label: 'All Organizations',
  type: 'All Organizations',
  icon: 'All Organizations'
};

/**
 * New-user / empty-org onboarding must resolve MSP scope so
 * CloudRestApiAccessProvider can load supportedFeatures (including MAPS).
 * Without this, newLogin only shows organization name and hides the map form.
 */
export function applyNewUserOnboardingScope(updateOrganizationContext) {
  if (typeof updateOrganizationContext !== 'function') {
    return;
  }
  updateOrganizationContext('', '0', '', ALL_ORGANIZATIONS);
}

/**
 * Choose the first authenticated page from the user's complete org/location hierarchy.
 * `organizationCount` comes from orgDetails because that endpoint is paginated.
 */
export function getPostLoginLanding(organizationCount, organizations = []) {
  const count = Number(organizationCount) || 0;

  if (count === 0) return { path: '/newLogin', scope: 'new-user' };
  if (count > 1) return { path: '/mspHome/dashboard', scope: 'msp' };

  const organization = organizations[0];
  if (!organization?.orgId || !organization?.orgName) {
    // Keep the established landing page if hierarchy details cannot be resolved.
    return { path: '/mspHome/dashboard', scope: 'msp' };
  }

  const locations = Array.isArray(organization.locations) ? organization.locations : [];
  if (locations.length === 1 && locations[0]?.id) {
    return {
      path: '/dashboard',
      scope: 'location',
      organization,
      location: locations[0]
    };
  }

  return {
    path: `/organization/${encodeURIComponent(organization.orgName)}/dashboard`,
    scope: 'organization',
    organization
  };
}

/** Build the same context payload used by a manual hierarchy selection. */
export function getPostLoginScopeState(landing) {
  if (!landing || landing.scope === 'new-user') return null;

  if (landing.scope === 'msp') {
    return {
      orgId: null,
      scopeId: '0',
      currentNetwork: null,
      selectedType: ALL_ORGANIZATIONS
    };
  }

  const organization = landing.organization;
  if (landing.scope === 'organization') {
    return {
      orgId: String(organization.orgId),
      scopeId: '1',
      currentNetwork: null,
      selectedType: {
        id: 'Organization-wide',
        label: organization.orgName,
        type: 'Organization-wide',
        icon: 'Org'
      }
    };
  }

  const location = landing.location;
  return {
    orgId: String(organization.orgId),
    scopeId: '2',
    currentNetwork: String(location.id),
    selectedType: { id: location.id, label: location.locationName, type: 'Loc', icon: 'Loc' }
  };
}

/** Keep the hierarchy selector, dashboard API scope, and landing URL in agreement. */
export function persistPostLoginScope(landing, storage = window.localStorage) {
  const scopeState = getPostLoginScopeState(landing);
  if (!scopeState) return null;

  storage.removeItem('currentDeviceGrpId');
  storage.removeItem('savedDeviceGrpName');

  if (landing.scope === 'msp') {
    storage.setItem('OrgId', '');
    storage.setItem('scopeId', '0');
    storage.setItem('currentNetwork', '');
    storage.removeItem('savedOrgName');
    storage.removeItem('savedLocationName');
    storage.setItem('selectedType', JSON.stringify(scopeState.selectedType));
    return scopeState;
  }

  const organization = landing.organization;
  storage.setItem('OrgId', scopeState.orgId);
  storage.setItem('savedOrgName', organization.orgName);

  if (landing.scope === 'organization') {
    storage.setItem('scopeId', scopeState.scopeId);
    storage.setItem('currentNetwork', '');
    storage.removeItem('savedLocationName');
    storage.setItem('selectedType', JSON.stringify(scopeState.selectedType));
    return scopeState;
  }

  const location = landing.location;
  storage.setItem('scopeId', scopeState.scopeId);
  storage.setItem('currentNetwork', scopeState.currentNetwork);
  storage.setItem('savedLocationName', location.locationName);
  storage.setItem('selectedType', JSON.stringify(scopeState.selectedType));
  return scopeState;
}
